import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, JSX } from 'react';
import { Formik } from 'formik';
import { useHistory, Prompt } from 'react-router-dom';

import { SubAccountCreationRequest } from './types';
import { SubAccountCreationDialogueHandler, DialogueHandlerProps } from '../../Dialogue/AccountCreation/types';
import SubAccountCreationDialogueContext, {
  SubAccountCreationCtx,
} from '../../Dialogue/AccountCreation/AccountCreationContext';
import SubAccountCreationDialogue from '../../Dialogue/AccountCreation/AccountCreationDialogue';
import usePostCreateSubAccount from '../../../../hooks/useApi/usePostCreateSubAccount';
import { useNavMenu } from '../../../../providers/NavMenu.provider';
import { usePendingData } from '../../../../providers/PendingData.provider';
import { Trans, useTranslation } from 'react-i18next';

const { Box, Table, Button, TextField } = vp;

type TablePropsHandlers = {
  creationDetails: () => vp.TableProps;
};

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible: boolean;
};

type TokenProps = {
  values: SubAccountCreationRequest;
};

type FC = FunctionComponent<TokenProps>;

const SubAccountCreationReview: FC = ({ values }: TokenProps) => {
  const history = useHistory();
  const { onSubNavClick } = useNavMenu();
  const [subAccountCreationResponse, postSubAccountCreation] = usePostCreateSubAccount({});
  const [shouldCreateSubAccount, setShouldCreateSubAccount] = useState(false);
  const [showSubAccountCreationSuccessAlert, setShowSubAccountCreationSuccessAlert] = useState(false);
  const [showSubAccountCreationErrorAlert, setShowSubAccountCreationErrorAlert] = useState(false);
  const [subAccountCreationError, setSubAccountCreationError] = useState('');
  const { hasPendingData } = usePendingData();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Create.Form' });

  const SubAccountCreation: SubAccountCreationCtx = {
    showSubAccountCreationSuccessAlert,
    showSubAccountCreationErrorAlert,
    subAccountCreationError,
  };

  const defaultValues = {
    LegalBusinessName: '',
    SubAccountType: '',
    OriginatorName: '',
    OriginatorShortName: '',
    EmailAddress: '',
    SendWelcomeEmail: true,
  };

  const handlers = {
    handleSubAccountCreationDialogue: (handlerProps: DialogueHandlerProps<SubAccountCreationDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;
      const dialogueHandlers: SubAccountCreationDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowSubAccountCreationErrorAlert(false);
              // onSubNavClick('subAccountsList', '/sub-accounts-list', 'Sub-Accounts List');
              // history.push('/sub-accounts-list');
            },
          },
        },
        success: {
          confirm: {
            default: () => {
              setShowSubAccountCreationSuccessAlert(false);
              onSubNavClick('subAccountsList', '/sub-accounts-list', 'Sub-Accounts List');
              history.push('/sub-accounts-list');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },

    onSubAccountCreation: (_values: SubAccountCreationRequest) => {
      setShouldCreateSubAccount(true);
      postSubAccountCreation({
        LegalBusinessName: values.LegalBusinessName,
        EmailAddress: values.EmailAddress,
        SendWelcomeEmail: values.SendWelcomeEmail,
        OriginatorName: values.OriginatorName,
        OriginatorShortName: values.OriginatorShortName,
      });
    },
  };

  useEffect(() => {
    if (shouldCreateSubAccount && subAccountCreationResponse) {
      const { Success, ErrorMessage } = subAccountCreationResponse;

      if (Success === true) {
        setShowSubAccountCreationSuccessAlert(true);
      } else if (Success === false) {
        setShowSubAccountCreationErrorAlert(true);
        setSubAccountCreationError(ErrorMessage);
      }
      setShouldCreateSubAccount(false);
    }
  }, [subAccountCreationResponse]);

  return (
    <Formik initialValues={defaultValues} onSubmit={handlers.onSubAccountCreation} validateOnChange validateOnBlur>
      {({ handleSubmit }) => (
        <SubAccountCreationDialogueContext.Provider value={SubAccountCreation}>
          <form onSubmit={handleSubmit}>
            <Box flx="col">
              <Box flx="col" width="50%" mt="md">
                <Box mb="md">
                  <Table {...getTableProps('creationDetails')} />
                </Box>
              </Box>
              <Box>
                <Trans
                  i18nKey="SubAccounts.Create.Form.termsAgreement"
                  ns="translations"
                  components={[
                    <a
                      href="https://vopay.com/ca/legal/term-of-use/"
                      style={{ textDecoration: 'underline', paddingLeft: 5 }}
                      target="_blank"
                      rel="noreferrer"
                    />,
                  ]}
                />
              </Box>
              <Box width="100%" jc="fe" mt="lg">
                <Button id="sub-account-creation-btn" label={t('submitButton')} type="submit" />
              </Box>
              <Prompt when={hasPendingData} message={t('pendingData')} />
            </Box>
          </form>
          <SubAccountCreationDialogue handleDialogue={handlers.handleSubAccountCreationDialogue} />
        </SubAccountCreationDialogueContext.Provider>
      )}
    </Formik>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlersTable: TablePropsHandlers = {
      creationDetails: () => {
        const creationDetailsPair: KeyValuePair[] = [
          {
            display: t('LegalBusinessName.label'),
            value: (
              <TextField
                name="business-name-confirmation"
                variant="background"
                placeholder={values.LegalBusinessName || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
            visible: true,
          },
          {
            display: t('OriginatorName.label'),
            value: (
              <TextField
                name="statement-descriptor-confirmation"
                variant="background"
                placeholder={values.OriginatorName || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
            visible: true,
          },
          {
            display: t('OriginatorShortName.label'),
            value: (
              <TextField
                name="statement-descriptor-confirmation"
                variant="background"
                placeholder={values.OriginatorShortName || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
            visible: true,
          },
          {
            display: t('EmailAddress.label'),
            value: (
              <TextField
                name="email-address-confirmation"
                variant="background"
                placeholder={values.EmailAddress || ''}
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
            visible: true,
          },
        ];

        const creationDetails: vp.TableProps = {
          name: 'sub-account-creation-details',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Sub-Account Creation Details' },
            detailValue: { header: '' },
          },
          rows:
            creationDetailsPair
              .filter(({ visible }) => visible)
              .map(({ display, value }) => ({
                detailKey: display,
                detailValue: value,
              })) || [],
        };

        return creationDetails;
      },
    };

    return handlersTable[handler]();
  }
};

export default SubAccountCreationReview;
